import { memo, useEffect, useState } from "react";
import styled from "styled-components";
import { Tag, Spin, Empty } from "antd";
import {
  S2MosaicQuarterly_Item,
  remote_S2MosaicQuarterly_GetPage,
} from "~/common/typescript/apis/RemoteImageAlertAPIs";
import S2MosaicQuarterlyItem from "./S2MosaicQuarterlyItem";
import S2MosaicQuarterlyFilterPanel from "./S2MosaicQuarterlyFilterPanel";
import { CalendarOutlined, AppstoreOutlined } from "@ant-design/icons";
import { useAtom, useSetAtom } from 'jotai';
import {
  satelliteRecordCountAtom,
  satelliteFilterTriggerAtom,
} from '../../store';
import {
  s2MosaicQuarterlyYearAtom,
  s2MosaicQuarterlyQuarterAtom,
} from './store';

const S2MosaicQuarterlyFinder: React.FC = () => {
  const [list, setList] = useState<S2MosaicQuarterly_Item[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [filterVisible, setFilterVisible] = useState(false);

  const [year, setYear] = useAtom(s2MosaicQuarterlyYearAtom);
  const [quarter, setQuarter] = useAtom(s2MosaicQuarterlyQuarterAtom);

  const setRecordCount = useSetAtom(satelliteRecordCountAtom);
  const setFilterTrigger = useSetAtom(satelliteFilterTriggerAtom);

  const fetchData = async (overrideYear?: number, overrideQuarter?: number) => {
    setLoading(true);
    try {
      const y = overrideYear !== undefined ? overrideYear : year;
      const q = overrideQuarter !== undefined ? overrideQuarter : quarter;
      const data = await remote_S2MosaicQuarterly_GetPage({
        year: y,
        quarter: q,
      });
      setList(data || []);
      setFilterVisible(false);
    } catch (err) {
      console.error('Failed to load S2 Mosaic Quarterly list:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Update record count when the list changes
  useEffect(() => {
    setRecordCount(list.length);
  }, [list, setRecordCount]);

  // Register the filter trigger function for top bar filter icon
  useEffect(() => {
    setFilterTrigger(() => {
      setFilterVisible(true);
    });
    return () => {
      setFilterTrigger(null);
    };
  }, [setFilterTrigger]);

  const handleResetFilter = () => {
    setYear(undefined);
    setQuarter(undefined);
    fetchData(undefined, undefined);
  };

  return (
    <>
      <UI_Wrapper>
        {/* Active filters display */}
        {(year !== undefined || quarter !== undefined) && (
          <div style={{ marginBottom: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {year !== undefined && (
              <Tag
                color="blue"
                icon={<CalendarOutlined />}
                closable
                onClose={() => {
                  setYear(undefined);
                  fetchData(undefined, quarter);
                }}
              >
                Năm: {year}
              </Tag>
            )}
            {quarter !== undefined && (
              <Tag
                color="green"
                icon={<AppstoreOutlined />}
                closable
                onClose={() => {
                  setQuarter(undefined);
                  fetchData(year, undefined);
                }}
              >
                Quý: {quarter}
              </Tag>
            )}
          </div>
        )}

        {/* Content list */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <UI_LoadingContainer>
              <Spin size="large" tip="Đang tải danh mục ảnh ghép quý..." />
            </UI_LoadingContainer>
          ) : list.length === 0 ? (
            <UI_EmptyContainer>
              <Empty description="Không tìm thấy ảnh ghép quý nào" />
            </UI_EmptyContainer>
          ) : (
            <UI_List_Wrapper>
              {list.map((item) => (
                <S2MosaicQuarterlyItem key={item.id} item={item} />
              ))}
            </UI_List_Wrapper>
          )}
        </div>
      </UI_Wrapper>

      {/* Filter drawer */}
      <S2MosaicQuarterlyFilterPanel
        visible={filterVisible}
        onClose={() => setFilterVisible(false)}
        year={year}
        setYear={setYear}
        quarter={quarter}
        setQuarter={setQuarter}
        onSearch={() => fetchData()}
        onReset={handleResetFilter}
      />
    </>
  );
};

export default memo(S2MosaicQuarterlyFinder);

const UI_Wrapper = styled.div`
  padding: 8px;
  height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const UI_List_Wrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const UI_LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 240px;
`;

const UI_EmptyContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 240px;
`;
